import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        logger.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url,
        });
      } catch (err) {
        logger.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  // Match a markdown image like ![alt](url). Used by renderContent below.
  // eslint-disable-next-line no-useless-escape
  const IMAGE_MARKDOWN_RE = /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)\s*$/;

  // Render a single line of inline content. Currently handles:
  //   • bare URLs that point to a static image become an <img>
  //   • markdown image syntax: ![alt](url)
  //   • everything else falls through as plain text
  const renderInline = (line, keyPrefix) => {
    const trimmed = line.trim();
    const m = trimmed.match(IMAGE_MARKDOWN_RE);
    if (m) {
      const [, alt, url] = m;
      return (
        <span key={keyPrefix} className="block my-6">
          <img
            src={url}
            alt={alt || ''}
            loading="lazy"
            className="w-full h-auto rounded-sm shadow-soft"
            data-testid="post-inline-image"
          />
          {alt && (
            <span className="block text-xs text-[#84A98C] italic text-center mt-2">
              {alt}
            </span>
          )}
        </span>
      );
    }
    return line;
  };

  // Convert markdown-like content to HTML
  const renderContent = (content) => {
    if (!content) return null;

    const paragraphs = content.split('\n\n');
    const nodes = [];
    let i = 0;
    while (i < paragraphs.length) {
      const paragraph = paragraphs[i];
      const trimmed = paragraph.trim();
      // Grid block:  <div class="blog-photo-grid"> ... </div>
      // The opener may be on its own line or followed inline by content;
      // we consume paragraphs until the matching </div> and treat every
      // image inside as a grid cell.
      if (trimmed.startsWith('<div class="blog-photo-grid">')) {
        const gridImages = [];
        // Global variant of the same pattern — unanchored so it matches
        // multiple images inside a single paragraph (which is how the
        // markdown ends up after split('\n\n') when images are separated
        // by a single newline).
        const globalImage = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;
        const collect = (str) => {
          if (!str) return;
          globalImage.lastIndex = 0;
          let m;
          while ((m = globalImage.exec(str)) !== null) {
            gridImages.push({ alt: m[1], url: m[2] });
          }
        };
        // First paragraph may include content after the opener
        collect(trimmed.replace(/^<div class="blog-photo-grid">/, ''));
        // Consume until we find the closing </div>
        i += 1;
        while (i < paragraphs.length) {
          const p = paragraphs[i].trim();
          if (p.includes('</div>')) {
            collect(p.replace(/<\/div>.*$/s, ''));
            break;
          }
          collect(p);
          i += 1;
        }
        // Render the grid — 2 columns on mobile, 3 on desktop.
        if (gridImages.length > 0) {
          nodes.push(
            <div
              key={`grid-${i}`}
              className="my-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
              data-testid="post-photo-grid"
            >
              {gridImages.map((g, gi) => (
                <figure key={gi} className="m-0">
                  <img
                    src={g.url}
                    alt={g.alt || ''}
                    loading="lazy"
                    className="w-full h-full aspect-square object-cover rounded-sm shadow-soft"
                    data-testid="post-inline-image"
                  />
                </figure>
              ))}
            </div>,
          );
        }
        i += 1;
        continue;
      }
      // Standalone image paragraph
      const imgMatch = paragraph.trim().match(IMAGE_MARKDOWN_RE);
      if (imgMatch) {
        const [, alt, url] = imgMatch;
        nodes.push(
          <figure key={i} className="my-8">
            <img
              src={url}
              alt={alt || ''}
              loading="lazy"
              className="w-full h-auto rounded-sm shadow-soft"
              data-testid="post-inline-image"
            />
            {alt && (
              <figcaption className="text-xs text-[#84A98C] italic text-center mt-2">
                {alt}
              </figcaption>
            )}
          </figure>,
        );
        i += 1;
        continue;
      }
      // Check for headers
      if (paragraph.startsWith('## ')) {
        nodes.push(
          <h2 key={i} className="font-serif text-2xl text-[#354F52] mt-8 mb-4">
            {paragraph.replace('## ', '')}
          </h2>,
        );
        i += 1;
        continue;
      }
      if (paragraph.startsWith('### ')) {
        nodes.push(
          <h3 key={i} className="font-serif text-xl text-[#354F52] mt-6 mb-3">
            {paragraph.replace('### ', '')}
          </h3>,
        );
        i += 1;
        continue;
      }
      // Check for list items
      if (paragraph.startsWith('- ')) {
        const items = paragraph.split('\n').filter((line) => line.startsWith('- '));
        nodes.push(
          <ul
            key={i}
            className="list-disc pl-6 space-y-2 text-[#52796F] leading-relaxed"
          >
            {items.map((item, li) => (
              <li key={li}>{item.replace('- ', '')}</li>
            ))}
          </ul>,
        );
        i += 1;
        continue;
      }
      // Regular paragraph — may still contain inline image markdown on its own line
      const lines = paragraph.split('\n');
      nodes.push(
        <p key={i} className="text-[#52796F] leading-relaxed">
          {lines.map((line, li) => (
            <span key={li}>
              {renderInline(line, `${i}-${li}`)}
              {li < lines.length - 1 && <br />}
            </span>
          ))}
        </p>,
      );
      i += 1;
    }
    return nodes;
  };

  if (loading) {
    return (
      <div className="pt-28 pb-16 min-h-screen">
        <div className="container-custom px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-4 bg-[#CAD2C5] rounded w-24 mb-8" />
            <div className="h-10 bg-[#CAD2C5] rounded w-3/4 mb-4" />
            <div className="h-4 bg-[#CAD2C5] rounded w-1/2 mb-8" />
            <div className="aspect-[16/9] bg-[#CAD2C5] rounded-sm mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-[#CAD2C5] rounded w-full" />
              <div className="h-4 bg-[#CAD2C5] rounded w-full" />
              <div className="h-4 bg-[#CAD2C5] rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-28 pb-16 min-h-screen">
        <div className="container-custom px-6 md:px-12 lg:px-24 text-center">
          <h1 className="font-serif text-2xl text-[#354F52] mb-4">
            Post not found
          </h1>
          <Link to="/blog" className="btn-secondary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16" data-testid="blog-post-page">
      <Seo
        title={post.title}
        description={(post.excerpt || post.content?.slice(0, 160) || '').replace(/\s+/g, ' ').trim()}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image && post.image.startsWith('/') ? `https://whisperingwristwear.com${post.image}` : post.image}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          image: post.image && post.image.startsWith('/') ? `https://whisperingwristwear.com${post.image}` : post.image,
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { '@type': 'Person', name: post.author || 'Lindy at Whispering Wristwear' },
          publisher: {
            '@type': 'Organization',
            name: 'Whispering Wristwear',
            logo: { '@type': 'ImageObject', url: 'https://whisperingwristwear.com/images/lindy-threading-hero.jpg' },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://whisperingwristwear.com/blog/${post.slug}` },
        }}
      />
      <article className="container-custom px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-8"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">Back to Blog</span>
        </Link>

        {/* Category */}
        <div className="mb-4">
          <span className="text-xs uppercase tracking-widest bg-[#F5F5F0] text-[#354F52] px-3 py-1.5 rounded-full">
            {post.category.replace('-', ' ')}
          </span>
        </div>

        {/* Title */}
        <h1 className="heading-display text-3xl md:text-4xl lg:text-5xl text-[#354F52] mb-6">
          {post.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-[#84A98C] mb-8 pb-8 border-b border-[#CAD2C5]/50">
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" strokeWidth={1.5} />
            {formatDate(post.created_at)}
          </span>
          <span className="flex items-center gap-2" data-testid="byline">
            <img
              src="/images/lindy-in-studio.jpg"
              alt="Lindy"
              className="w-7 h-7 rounded-full object-cover ring-1 ring-[#D4AF37]/40"
              style={{ objectPosition: '50% 30%' }}
            />
            <span>{post.author}</span>
          </span>
          <button 
            onClick={handleShare}
            className="flex items-center gap-2 hover:text-[#354F52] transition-colors ml-auto"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4" strokeWidth={1.5} />
            Share
          </button>
        </div>

        {/* Featured Image */}
        <div className="mb-10">
          <img
            src={post.image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover rounded-sm"
          />
        </div>

        {/* Excerpt */}
        <p className="text-xl text-[#354F52] leading-relaxed mb-8 font-serif italic">
          {post.excerpt}
        </p>

        {/* Content */}
        <div className="prose prose-lg max-w-none space-y-4">
          {renderContent(post.content)}
        </div>

        {/* CTA */}
        <div className="mt-16 p-8 bg-[#F5F5F0] rounded-sm text-center">
          <h3 className="font-serif text-2xl text-[#354F52] mb-4">
            Ready to Create Your Own?
          </h3>
          <p className="text-[#52796F] mb-6">
            Explore our collection or design a custom Morse code bracelet with your special message.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="btn-primary" data-testid="shop-now-cta">
              Shop Now
            </Link>
            <Link to="/custom" className="btn-secondary" data-testid="custom-order-cta">
              Create Custom
            </Link>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="mt-12 pt-8 border-t border-[#CAD2C5]/50">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm uppercase tracking-widest">More Articles</span>
          </Link>
        </div>
      </article>
    </div>
  );
}
